/**
 * Um item da ata na tela interna: o texto, para onde ele vai, e o que se pode fazer com ele.
 *
 * **A marca de destino vem antes do texto**, e não depois. Quem passa os olhos numa lista de dez
 * itens lê o começo de cada linha; deixar a marca no fim faria a leitura acontecer na ordem
 * errada — primeiro a opinião, depois o aviso de que ela é interna.
 *
 * **O vínculo com o Jira só aparece em pendência**, porque só ela é cobrança: `responsavel_lado`,
 * `prazo` e a chave da issue são recusados pelo servidor em decisão e nota interna
 * (`MENSAGEM_DE_CAMPO_FORA_DA_PENDENCIA`). E ele continua disponível **depois de publicada**: a
 * chave é informação sobre a tarefa, não sobre o que foi dito.
 */

import { useState } from "react";
import { Aviso, Botao, Campo, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DO_LADO,
  ROTULO_DO_TIPO_DE_ITEM,
  destinoDoItem,
  estadoDaTarefa,
  formatarMinuto,
  formatarPrazo,
  useRemoverItem,
  useVincularTarefa,
  type Ata,
  type ItemDaAta,
} from "../../nucleo/reuniao";
import { FraseDoDestino, MarcaDeDestino, MarcaDeNotaInterna } from "./MarcaDeDestino";
import { FormularioDoItem } from "./FormularioDoItem";

export interface PropsDoItemNaAta {
  ata: Ata;
  item: ItemDaAta;
  podeEditar: boolean;
  podeEscreverNotaInterna: boolean;
}

export function ItemNaAta({ ata, item, podeEditar, podeEscreverNotaInterna }: PropsDoItemNaAta) {
  const [corrigindo, setCorrigindo] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const remover = useRemoverItem(ata.id);
  const destino = destinoDoItem(item);

  if (corrigindo) {
    return (
      <FormularioDoItem
        ataId={ata.id}
        item={item}
        podeEscreverNotaInterna={podeEscreverNotaInterna}
        aoFechar={() => setCorrigindo(false)}
      />
    );
  }

  return (
    <article
      className="item-da-ata"
      data-destino={destino}
      data-tipo={item.tipo}
      data-testid={`item-${item.id}`}
    >
      <header className="item-da-ata__cabecalho">
        <MarcaDeDestino destino={destino} />
        {/* O selo do tipo mais caro de errar, e só nele. Redundante com a marca ao lado de
            propósito: quem procura "onde está a nota interna desta ata" tem de achá-la sem ler
            item por item. */}
        {item.tipo === "nota_interna" ? (
          <MarcaDeNotaInterna />
        ) : (
          <Chip estado="neutro" semPonto>
            {ROTULO_DO_TIPO_DE_ITEM[item.tipo]}
          </Chip>
        )}
        {item.minuto !== null ? (
          <span className="nota-rodape numero-tabular">{formatarMinuto(item.minuto)}</span>
        ) : null}
      </header>

      <p className="item-da-ata__texto">{item.texto}</p>

      {item.tipo === "pendencia" ? (
        <p className="item-da-ata__meta" data-testid={`dados-da-pendencia-${item.id}`}>
          <b>Responsável:</b> {item.responsavel ?? "não nomeado"}
          {item.responsavel_lado ? ` (${ROTULO_DO_LADO[item.responsavel_lado]})` : ""}
          {item.prazo ? (
            <>
              {" · "}
              <b>Prazo:</b> <span className="numero-tabular">{formatarPrazo(item.prazo)}</span>
            </>
          ) : null}
        </p>
      ) : null}

      <FraseDoDestino destino={destino} tipo={item.tipo} />

      {/* Pendência do cliente que não atravessa é o defeito mais silencioso da fatia: o servidor
          NÃO abre evento na esteira dela — cobrar alguém por um texto que ele não pode ler é
          pior do que não cobrar — e sem este aviso quem publicou acharia que cobrou. */}
      {item.tipo === "pendencia" &&
      item.responsavel_lado === "cliente" &&
      destino === "interno" ? (
        <Aviso tom="atencao" className="mt-3" data-testid={`pendencia-muda-${item.id}`}>
          <b>Esta cobrança é do cliente e ele não vai vê-la.</b> Como o item não vai ao cliente, a
          publicação não abre pendência na esteira dele — a cobrança fica só aqui dentro. Marque
          &ldquo;o cliente vê&rdquo; se a intenção é cobrar de verdade.
        </Aviso>
      ) : null}

      {item.tipo === "pendencia" ? (
        <VinculoComOJira
          ata={ata}
          item={item}
          podeEditar={podeEditar}
          abrindo={vinculando}
          aoAbrir={setVinculando}
        />
      ) : null}

      {podeEditar && !ata.publicada ? (
        <div className="item-da-ata__acoes">
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            onClick={() => setCorrigindo(true)}
            data-testid={`corrigir-${item.id}`}
          >
            Corrigir
          </Botao>
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            disabled={remover.isPending}
            onClick={() => remover.mutate(item.id)}
            data-testid={`remover-${item.id}`}
          >
            {remover.isPending ? "Removendo…" : "Remover"}
          </Botao>
          {remover.isError ? (
            <span className="nota-rodape">{mensagemDoConsole(remover.error)}</span>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

/**
 * A pendência e a tarefa dela — "pendências viraram tarefa", com o Jira desligado.
 *
 * **O portal não cria issue de pendência no ONB** (ADR 0002: quem cria lá é a Automation for
 * Jira, e um segundo criador duplicaria issue num projeto de outros times). O que funciona é
 * colar a chave da issue que já existe. Pedir a abertura devolve `200` com o motivo escrito, e a
 * tela mostra isso como **pendência**, nunca como erro: chamar de falha o estado normal deste
 * servidor ensina quem opera a ignorar alarme.
 */
function VinculoComOJira({
  ata,
  item,
  podeEditar,
  abrindo,
  aoAbrir,
}: {
  ata: Ata;
  item: ItemDaAta;
  podeEditar: boolean;
  abrindo: boolean;
  aoAbrir: (aberto: boolean) => void;
}) {
  const [chave, setChave] = useState(item.jira_key ?? "");
  const vincular = useVincularTarefa(ata.id);
  const tarefa = estadoDaTarefa(item);

  return (
    <div className="item-da-ata__meta" data-testid={`tarefa-${item.id}`}>
      {tarefa.estado === "vinculada" ? (
        <>
          <b>Tarefa:</b> <code>{tarefa.chave}</code>
        </>
      ) : tarefa.estado === "pendente" ? (
        <>
          <b>Tarefa pendente:</b> {tarefa.motivo}
        </>
      ) : (
        <>
          <b>Sem tarefa vinculada.</b> Quando a tarefa existir no Jira, cole a chave aqui.
        </>
      )}

      {podeEditar ? (
        abrindo ? (
          <div className="item-da-ata__acoes">
            <Campo
              rotulo="Chave da tarefa no Jira"
              value={chave}
              onChange={(evento) => setChave(evento.target.value)}
              placeholder="ONB-1234"
              classeDaMoldura="flex-1"
              data-testid={`chave-do-jira-${item.id}`}
            />
            <Botao
              tamanho="pequeno"
              disabled={vincular.isPending || chave.trim().length < 3}
              onClick={() =>
                vincular.mutate(
                  { itemId: item.id, jiraKey: chave.trim() },
                  { onSuccess: () => aoAbrir(false) },
                )
              }
              data-testid={`vincular-${item.id}`}
            >
              {vincular.isPending ? "Vinculando…" : "Vincular"}
            </Botao>
            <Botao variante="fantasma" tamanho="pequeno" onClick={() => aoAbrir(false)}>
              Cancelar
            </Botao>
            {vincular.isError ? (
              <span className="nota-rodape">{mensagemDoConsole(vincular.error)}</span>
            ) : null}
          </div>
        ) : (
          <div className="item-da-ata__acoes">
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => aoAbrir(true)}
              data-testid={`abrir-vinculo-${item.id}`}
            >
              {item.jira_key ? "Trocar a tarefa" : "Vincular a uma tarefa do Jira"}
            </Botao>
          </div>
        )
      ) : null}
    </div>
  );
}
